import React, { useState, useEffect } from 'react';
import { Button } from 'components/atoms/index';
import Toast from '@/components/atoms/Toast/Toast';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import { formatNumber } from '@/utils/number';
import { studentExamApi } from '@/services/api';
import ListenButton from '@/components/atoms/ListenButton/ListenButton';

// 학생 시험 결과 상세 페이지
export default function StudentExamResultDetail() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const slugPrefix = getSlugPrefix(pathname);
    const [searchParams] = useSearchParams();
    const examResultId = searchParams.get('examResultId');
    const [isVisible, setIsVisible] = useState(false);
    const [toastStatus, setToastStatus] = useState<'success' | 'error' | 'info'>('success');
    const [toastMessage, setToastMessage] = useState('');

    const [studentName, setStudentName] = useState('');
    const [studentLoginId, setStudentLoginId] = useState('');
    const [examName, setExamName] = useState('');
    const [completionDate, setCompletionDate] = useState('');
    const [gradingStatus, setGradingStatus] = useState('');
    const [timeSpent, setTimeSpent] = useState('');
    const [items, setItems] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 6;
    
    // 채점 가이드
    const [guideline, setGuideline] = useState({
        objective: '',
        subjective: '',
        essay: ''
    });
    const [commentMap, setCommentMap] = useState<Record<string, string>>({});
    const [pendingScrollTarget, setPendingScrollTarget] = useState<string | null>(null);

    useEffect(() => {
        if (!examResultId) {
            navigate(`${slugPrefix}/mypage/exam`);
            return;
        }
        loadGradingDetail(parseInt(examResultId, 10));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [examResultId]);

    const loadGradingDetail = async (id: number) => {
        try {
            const res: any = await studentExamApi.detail(id);
            const data = res?.data || res;
            
            const storedName = typeof window !== 'undefined' ? (localStorage.getItem('student_user_name') || '') : '';
            const storedLoginId = typeof window !== 'undefined'
                ? (localStorage.getItem('student_login_id')
                    || localStorage.getItem('student_email')
                    || '')
                : '';

            setStudentName(data?.studentName || storedName);
            setStudentLoginId(data?.studentLoginId || storedLoginId);
            setExamName(data?.examName || '');
            setCompletionDate(data?.completionDate || '');
            setGradingStatus(data?.gradingStatus || '');
            setTimeSpent(data?.timeSpent || '');
            
            const rawItems = Array.isArray(data?.items) ? data.items : [];
            setItems(rawItems);

            // 저장된 채점 가이드 불러오기
            if (data?.gradingGuideline) {
                try {
                    const savedGuideline = JSON.parse(data.gradingGuideline);
                    setGuideline({
                        objective: savedGuideline.objective || '',
                        subjective: savedGuideline.subjective || '',
                        essay: savedGuideline.essay || ''
                    });
                    setCommentMap(savedGuideline.comments || {});
                } catch (e) {
                    console.error('채점 가이드 파싱 실패:', e);
                }
            }
        } catch (e) {
            setToastStatus('error');
            setToastMessage('시험 정보를 불러올 수 없습니다.');
            setIsVisible(true);
        }
    };

    const mapQuestionType = (qt: string): 'multiple' | 'short' | 'essay' => {
        switch ((qt || '').toUpperCase()) {
            case 'MULTIPLE_CHOICE':
                return 'multiple';
            case 'SHORT_ANSWER':
                return 'short';
            case 'ESSAY':
                return 'essay';
            default:
                return 'short';
        }
    };

    const parseChoices = (choices: any): string[] => {
        if (!choices) return [];
        if (Array.isArray(choices)) return choices as string[];
        try {
            const parsed = JSON.parse(choices);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    };

    const parseAnswer = (answer: any): any => {
        if (!answer) return null;
        if (typeof answer === 'string') {
            try {
                return JSON.parse(answer);
            } catch {
                return answer;
            }
        }
        return answer;
    };

    const parseStudentAnswer = (answerText: any): any => {
        if (!answerText) return null;
        if (typeof answerText === 'string') {
            try {
                return JSON.parse(answerText);
            } catch {
                return answerText;
            }
        }
        return answerText;
    };

    // 정답 포맷팅 (0-based index를 1-based로 표시)
    const formatAnswer = (answer: any, choices: string[]): string => {
        if (answer === null || answer === undefined) return '-';
        if (choices.length === 0) return String(answer);
        
        // 배열인 경우 (복수 정답 객관식) - 0-based index를 1-based로 표시
        if (Array.isArray(answer)) {
            const indices = answer.map(a => {
                if (typeof a === 'number') return a;
                if (typeof a === 'string') {
                    const num = parseInt(a, 10);
                    return isNaN(num) ? -1 : num;
                }
                return -1;
            });
            const texts = indices
                .filter(i => i >= 0 && i < choices.length)
                .map(i => `${i + 1}. ${choices[i]}`);
            return texts.length > 0 ? texts.join(', ') : '-';
        }
        
        // 숫자인 경우 (단일 정답 객관식) - 0-based index를 1-based로 표시
        if (typeof answer === 'number') {
            if (answer >= 0 && answer < choices.length) {
                return `${answer + 1}. ${choices[answer]}`;
            }
            return String(answer);
        }
        
        // 문자열인 경우 - 0-based index를 1-based로 표시
        if (typeof answer === 'string') {
            const num = parseInt(answer, 10);
            if (!isNaN(num) && num >= 0 && num < choices.length) {
                return `${num + 1}. ${choices[num]}`;
            }
            return answer;
        }
        
        return String(answer);
    };

    // 학생 답안 포맷팅 (1-based로 저장되어 있으므로 그대로 표시)
    const formatStudentAnswer = (answer: any, choices: string[]): string => {
        if (answer === null || answer === undefined) return '-';
        if (choices.length === 0) return String(answer);
        
        // 배열인 경우 (복수 정답) - 1-based로 저장됨
        if (Array.isArray(answer)) {
            const indices = answer.map(a => {
                if (typeof a === 'number') return a;
                if (typeof a === 'string') {
                    const num = parseInt(a, 10);
                    return isNaN(num) ? 0 : num;
                }
                return 0;
            });
            const texts = indices
                .filter(i => i >= 1 && i <= choices.length)
                .map(i => `${i}. ${choices[i - 1]}`);
            return texts.length > 0 ? texts.join(', ') : '-';
        }
        
        // 숫자인 경우 (단일 정답) - 1-based로 저장됨
        if (typeof answer === 'number') {
            if (answer >= 1 && answer <= choices.length) {
                return `${answer}. ${choices[answer - 1]}`;
            }
            return String(answer);
        }
        
        // 문자열인 경우 - 1-based로 저장됨
        if (typeof answer === 'string') {
            const num = parseInt(answer, 10);
            if (!isNaN(num) && num >= 1 && num <= choices.length) {
                return `${num}. ${choices[num - 1]}`;
            }
            return answer;
        }
        
        return String(answer);
    };

    const wrongItems = gradingStatus === 'completed'
        ? items
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => item?.studentAnswerItemId && item?.isCorrect === false)
        : [];

    const handleJumpToQuestion = (absoluteIndex: number) => {
        const nextPage = Math.floor(absoluteIndex / pageSize) + 1;
        setCurrentPage(nextPage);
        setPendingScrollTarget(`exam-question-${absoluteIndex}`);
    };

    useEffect(() => {
        if (!pendingScrollTarget) return;
        const timer = setTimeout(() => {
            const target = document.getElementById(pendingScrollTarget);
            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                target.classList.add('ring-2', 'ring-indigo-400');
                setTimeout(() => target.classList.remove('ring-2', 'ring-indigo-400'), 1500);
            }
            setPendingScrollTarget(null);
        }, 200);
        return () => clearTimeout(timer);
    }, [pendingScrollTarget]);

    return (
        <main className='flex flex-col w-full min-h-screen bg-white md:bg-transparent md:py-10'>
            <div className='max-w-7xl w-full mx-auto px-5'>
                {/* 상단 헤더 */}

                {/* 전체 성적표 컨테이너 - 상단 메뉴와 유사한 디자인 */}
                <div className='bg-white rounded-t-2xl md:rounded-2xl shadow-sm border border-gray-200 overflow-hidden'>
                <div className='flex items-center gap-3 mb-3 mt-8 ml-6'>
                    <Button outline size='small' onClick={() => navigate(`${slugPrefix}/mypage/exam`)}>
                        ← 목록으로
                    </Button>
                </div>
                    {/* 채점 완료 시 성적표 */}
                    {gradingStatus === 'completed' && (() => {
                const hasMultiple = items.some(item => mapQuestionType(item?.questionType) === 'multiple');
                const hasShort = items.some(item => mapQuestionType(item?.questionType) === 'short');
                const hasEssay = items.some(item => mapQuestionType(item?.questionType) === 'essay');
                
                const multipleItems = items.filter(item => mapQuestionType(item?.questionType) === 'multiple');
                const shortItems = items.filter(item => mapQuestionType(item?.questionType) === 'short');
                const essayItems = items.filter(item => mapQuestionType(item?.questionType) === 'essay');
                
                const multipleScore = multipleItems.reduce((sum, item) => sum + (item.givenScore || 0), 0);
                const multipleCorrect = multipleItems.filter(item => item.isCorrect === true).length;
                const multipleRate = multipleItems.length > 0 ? Math.round((multipleCorrect / multipleItems.length) * 100) : 0;
                
                const shortScore = shortItems.reduce((sum, item) => sum + (item.givenScore || 0), 0);
                const shortCorrect = shortItems.filter(item => item.isCorrect === true).length;
                const shortRate = shortItems.length > 0 ? Math.round((shortCorrect / shortItems.length) * 100) : 0;
                
                const essayScore = essayItems.reduce((sum, item) => sum + (item.givenScore || 0), 0);
                const essayCorrect = essayItems.filter(item => item.isCorrect === true).length;
                const essayRate = essayItems.length > 0 ? Math.round((essayCorrect / essayItems.length) * 100) : 0;
                
                const wrongMultiple = multipleItems.map((item, idx) => (item.givenScore || 0) < (item.maxScore || 0) ? `${idx + 1}번` : '').filter(Boolean).join(', ') || '없음';
                const wrongShort = shortItems.map((item, idx) => (item.givenScore || 0) < (item.maxScore || 0) ? `${idx + 1}번` : '').filter(Boolean).join(', ') || '없음';
                
                return (
                    <div className='p-6'>
                        {/* 성적표 헤더 */}
                        <div className='px-6 py-6 bg-orange-50 border border-orange-200 rounded-2xl shadow-sm mb-6'>
                            <div className='flex justify-between items-start'>
                                <div>
                                    <h2 className='text-2xl font-bold text-orange-900 mb-2'>
                                        {examName} 시험 성적표
                                    </h2>
                                    <p className='text-sm text-gray-600'>{completionDate} 응시완료 · 소요시간: {timeSpent}</p>
                                    <div className='flex flex-wrap gap-3 mt-2 text-sm text-gray-700'>
                                        <span className='font-semibold text-gray-900'>학생명</span>
                                        <span>{studentName || '정보 없음'}</span>
                                        <span className='text-gray-300'>|</span>
                                        <span className='font-semibold text-gray-900'>아이디</span>
                                        <span>{studentLoginId || '정보 없음'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* 채점 가이드 (읽기 전용) */}
                        <div className='space-y-4 mb-6'>
                            {hasMultiple && guideline.objective && (
                                <div className='p-4 bg-white rounded-2xl border border-orange-300 shadow-sm'>
                                    <h4 className='font-bold text-orange-900 mb-2'>📌 {examName}_객관식 (독점/총점)</h4>
                                    <p className='text-sm text-gray-700 whitespace-pre-wrap'>{guideline.objective}</p>
                                </div>
                            )}
                            {hasShort && guideline.subjective && (
                                <div className='p-4 bg-white rounded-2xl border border-orange-300 shadow-sm'>
                                    <h4 className='font-bold text-orange-900 mb-2'>📌 {examName}_주관식 (독점/총점)</h4>
                                    <p className='text-sm text-gray-700 whitespace-pre-wrap'>{guideline.subjective}</p>
                                </div>
                            )}
                            {hasEssay && guideline.essay && (
                                <div className='p-4 bg-white rounded-2xl border border-orange-300 shadow-sm'>
                                    <h4 className='font-bold text-orange-900 mb-2'>📌 {examName}_프리라이팅 (독점/총점)</h4>
                                    <p className='text-sm text-gray-700 whitespace-pre-wrap'>{guideline.essay}</p>
                                </div>
                            )}
                        </div>
                        
                        {/* 성적 표 & 그래프 */}
                        <div className='grid md:grid-cols-2 gap-6 mb-6'>
                            {/* 성적 표 */}
                            <div className='bg-white rounded-2xl shadow-sm overflow-hidden'>
                                <table className='w-full border-collapse'>
                                    <thead>
                                        <tr className='bg-gray-100'>
                                            <th className='border border-gray-300 px-4 py-3 text-left text-sm font-bold'>구분</th>
                                            {hasMultiple && <th className='border border-gray-300 px-4 py-3 text-center text-sm font-bold'>객관식</th>}
                                            {hasShort && <th className='border border-gray-300 px-4 py-3 text-center text-sm font-bold'>주관식</th>}
                                            {hasEssay && <th className='border border-gray-300 px-4 py-3 text-center text-sm font-bold'>프리라이팅</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className='border border-gray-300 px-4 py-2 font-medium text-sm'>총점</td>
                                            {hasMultiple && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{formatNumber(multipleScore)}점</td>}
                                            {hasShort && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{formatNumber(shortScore)}점</td>}
                                            {hasEssay && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{formatNumber(essayScore)}점</td>}
                                        </tr>
                                        <tr>
                                            <td className='border border-gray-300 px-4 py-2 font-medium text-sm'>독점</td>
                                            {hasMultiple && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{formatNumber(multipleCorrect)}개</td>}
                                            {hasShort && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{formatNumber(shortCorrect)}개</td>}
                                            {hasEssay && <td className='border border-gray-300 px-4 py-2 text-center text-sm'>{formatNumber(essayCorrect)}개</td>}
                                        </tr>
                                        <tr className='bg-orange-50'>
                                            <td className='border border-gray-300 px-4 py-2 font-bold text-sm'>감점받은 문제</td>
                                            {hasMultiple && <td className='border border-gray-300 px-4 py-2 text-center font-bold text-sm'>{wrongMultiple}</td>}
                                            {hasShort && <td className='border border-gray-300 px-4 py-2 text-center font-bold text-sm'>{wrongShort}</td>}
                                            {hasEssay && <td className='border border-gray-300 px-4 py-2 text-center font-bold text-sm'>하단 문제 참고</td>}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* 그래프 */}
                            <div className='flex items-end justify-around h-64 border border-gray-300 rounded-2xl p-6 bg-white shadow-sm'>
                                {hasMultiple && (
                                    <div className='flex flex-col items-center'>
                                        <div className='w-20 bg-indigo-400 rounded-t-lg' style={{ height: `${Math.min(multipleRate, 100)}%`, minHeight: '20px' }}></div>
                                        <p className='text-lg font-bold mt-3 text-indigo-600'>{multipleRate}%</p>
                                        <p className='text-xs text-gray-600 mt-1'>객관식</p>
                                    </div>
                                )}
                                {hasShort && (
                                    <div className='flex flex-col items-center'>
                                        <div className='w-20 bg-blue-400 rounded-t-lg' style={{ height: `${Math.min(shortRate, 100)}%`, minHeight: '20px' }}></div>
                                        <p className='text-lg font-bold mt-3 text-blue-600'>{shortRate}%</p>
                                        <p className='text-xs text-gray-600 mt-1'>주관식</p>
                                    </div>
                                )}
                                {hasEssay && (
                                    <div className='flex flex-col items-center'>
                                        <div className='w-20 bg-purple-400 rounded-t-lg' style={{ height: `${Math.min(essayRate, 100)}%`, minHeight: '20px' }}></div>
                                        <p className='text-lg font-bold mt-3 text-purple-600'>{essayRate}%</p>
                                        <p className='text-xs text-gray-600 mt-1'>프리라이팅</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {gradingStatus === 'completed' && wrongItems.length > 0 && (
                            <div className='mt-6 rounded-2xl border border-red-100 bg-red-50 p-4'>
                                <div className='flex flex-wrap items-center justify-between gap-2 mb-3'>
                                    <p className='text-sm font-semibold text-red-600'>감점된 문제</p>
                                    <p className='text-xs text-red-500'>버튼을 누르면 해당 문항 카드로 이동합니다.</p>
                                </div>
                                <div className='flex flex-wrap gap-2'>
                                    {wrongItems.map(({ index }) => (
                                        <button
                                            key={`wrong-${index}`}
                                            type='button'
                                            className='px-3 py-1 text-sm font-medium bg-white border border-red-200 rounded-full text-red-600 hover:bg-red-100'
                                            onClick={() => handleJumpToQuestion(index)}
                                        >
                                            문항 {index + 1}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}

                    {/* 채점 대기 중 안내 */}
                    {gradingStatus !== 'completed' && (
                        <div className='p-6'>
                            <div className='px-6 py-6 bg-yellow-50 border border-yellow-200 rounded-2xl shadow-sm'>
                                <h3 className='text-lg font-bold text-yellow-900 mb-2'>📝 채점 대기 중</h3>
                                <p className='text-sm text-gray-700'>선생님이 채점을 완료하면 성적표를 확인할 수 있습니다.</p>
                            </div>
                        </div>
                    )}

                    {/* 문제 카드 section - 페이징 (6문항/페이지, 좌 3개/우 3개) */}
                    {(() => {
                const start = (currentPage - 1) * pageSize;
                const end = start + pageSize;
                const pageItems = items.slice(start, end);
                const leftItems = pageItems.slice(0, Math.min(3, pageItems.length));
                const rightItems = pageItems.slice(Math.min(3, pageItems.length));
                
                const renderCard = (item: any, absoluteIdx: number) => {
                    const qType = mapQuestionType(item?.questionType);
                    const choices = parseChoices(item?.optionsJson);
                    const hasImage = !!item?.imageUrl;
                    const hasListen = !!item?.audioUrl;
                    const questionText = `${absoluteIdx + 1}. ${item?.questionText || ''}`;
                    
                    const correctAnswer = parseAnswer(item?.correctAnswerJson);
                    const studentAnswer = parseStudentAnswer(item?.studentAnswer);
                    
                    // 정답: 0-based index로 저장됨
                    const correctAnswerText = qType === 'multiple' 
                        ? formatAnswer(correctAnswer, choices)
                        : (typeof correctAnswer === 'string' ? correctAnswer : JSON.stringify(correctAnswer));
                    
                    // 학생 답안: 1-based로 저장되므로 변환 필요
                    const studentAnswerText = qType === 'multiple'
                        ? formatStudentAnswer(studentAnswer, choices)
                        : (studentAnswer ? String(studentAnswer) : '-');

                    const maxScore = item?.maxScore || 0;
                    const givenScore = item?.givenScore;
                    const isCorrect = item?.isCorrect;

                    const commentKey = item?.studentAnswerItemId ? String(item.studentAnswerItemId) : '';
                    const teacherComment = commentKey ? commentMap[commentKey] : '';
                    return (
                        <div
                            key={item?.paperItemId || absoluteIdx}
                            id={`exam-question-${absoluteIdx}`}
                            className='flex flex-col gap-4 p-5 bg-white rounded-2xl border border-gray-200 shadow-sm'
                        >
                            {/* 문제 표시 */}
                            <div>
                                <h3 className='text-lg font-bold mb-4 text-gray-800'>{questionText}</h3>
                                
                                {/* 오디오만 있는 경우 */}
                                {!hasImage && hasListen && item?.audioUrl && (
                                    <div className='mb-4'>
                                        <ListenButton size={36} audioSrc={item.audioUrl} />
                                    </div>
                                )}
                                
                                {/* 이미지와 오디오 */}
                                {hasImage && item?.imageUrl && (
                                    <div className='flex justify-center mb-4'>
                                        <div className='relative'>
                                            <img 
                                                src={item.imageUrl} 
                                                alt="question" 
                                                className='max-w-[200px] rounded-lg border border-gray-200' 
                                            />
                                            {hasListen && item?.audioUrl && (
                                                <div className='absolute -top-4 -right-4'>
                                                    <ListenButton size={40} audioSrc={item.audioUrl} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                {/* 객관식 보기 (읽기 전용) */}
                                {qType === 'multiple' && choices.length > 0 && (
                                    <div className='flex flex-col gap-2 mb-4'>
                                        {choices.map((choice, idx) => (
                                            <div key={idx} className='flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200'>
                                                <span className='flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold'>
                                                    {idx + 1}
                                                </span>
                                                <span className='font-medium text-gray-700'>{choice}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {/* 주관식/서술형 답안 안내 */}
                                {qType !== 'multiple' && (
                                    <div className='p-3 mb-4 bg-blue-50 rounded-lg border border-blue-200'>
                                        <p className='text-sm text-blue-700 font-medium'>
                                            {qType === 'essay' ? '📝 서술형 문항입니다. 아래에서 답안을 확인하세요.' : '✍️ 주관식 문항입니다. 아래에서 답안을 확인하세요.'}
                                        </p>
                                    </div>
                                )}
                            </div>
                            
                            {/* 정답과 학생 답안 표시 */}
                            <div className='p-4 bg-gray-50 rounded-xl border border-gray-200'>
                                {/* 채점 완료 시에만 정답 표시 */}
                                {gradingStatus === 'completed' && (
                                    qType === 'multiple' ? (
                                        <div className='grid grid-cols-2 gap-4 mb-4'>
                                            <div>
                                                <p className='text-xs text-gray-500 mb-1'>정답</p>
                                                <p className='text-sm font-semibold text-green-700'>
                                                    {correctAnswerText}
                                                </p>
                                            </div>
                                            <div>
                                                <p className='text-xs text-gray-500 mb-1'>내 답안</p>
                                                <p className='text-sm font-semibold text-blue-700'>
                                                    {studentAnswerText}
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className='space-y-3 mb-4'>
                                            <div>
                                                <p className='text-xs text-gray-500 mb-2'>정답</p>
                                                <div className='p-3 bg-green-50 rounded border border-green-200'>
                                                    <p className='text-sm font-medium text-green-800 whitespace-pre-wrap'>
                                                        {correctAnswerText}
                                                    </p>
                                                </div>
                                            </div>
                                            <div>
                                                <p className='text-xs text-gray-500 mb-2'>내 답안</p>
                                                <div className='p-3 bg-blue-50 rounded border border-blue-200'>
                                                    <p className='text-sm font-medium text-blue-800 whitespace-pre-wrap'>
                                                        {studentAnswerText}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                )}

                                {/* 채점 대기 중: 학생 답안만 표시 */}
                                {gradingStatus !== 'completed' && (
                                    <div>
                                        <p className='text-xs text-gray-500 mb-2'>내 답안</p>
                                        <div className='p-3 bg-blue-50 rounded border border-blue-200'>
                                            <p className='text-sm font-medium text-blue-800 whitespace-pre-wrap'>
                                                {studentAnswerText}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* 채점 완료 시 채점 결과 표시 */}
                                {gradingStatus === 'completed' && item.studentAnswerItemId && givenScore !== null && givenScore !== undefined && (
                                    <div className='pt-3 border-t border-gray-300'>
                                        <div className='flex items-center justify-between'>
                                            <span className={`px-4 py-2 rounded-lg font-bold text-sm ${
                                                isCorrect === true 
                                                    ? 'bg-green-100 text-green-700' 
                                                    : 'bg-red-100 text-red-700'
                                            }`}>
                                                {isCorrect ? '✓ 정답' : '✗ 오답'}
                                            </span>
                                            <span className='text-base font-bold text-gray-700'>
                                                <span className='text-indigo-600'>{formatNumber(givenScore)}</span> / {formatNumber(maxScore)}점
                                            </span>
                                        </div>
                                    </div>
                                )}
                                
                                {!item.studentAnswerItemId && (
                                    <div className='pt-3 text-sm text-gray-500 border-t border-gray-300'>
                                        답안 없음
                                    </div>
                                )}
                            </div>
                            {teacherComment && (
                                <div className='p-4 bg-white rounded-xl border border-orange-200'>
                                    <p className='text-xs font-semibold text-orange-500'>교사 코멘트</p>
                                    <p className='text-sm text-gray-800 whitespace-pre-wrap mt-1'>{teacherComment}</p>
                                </div>
                            )}
                        </div>
                    );
                };
                
                        return (
                            <section className='lg:grid lg:grid-cols-2 lg:gap-0 *:py-5 lg:*:pb-10 *:px-5'>
                                <div className='flex flex-col gap-5'>
                                    {leftItems.map((it, i) => renderCard(it, start + i))}
                                </div>
                                <div className='flex flex-col gap-5'>
                                    {rightItems.map((it, i) => renderCard(it, start + 3 + i))}
                                </div>
                            </section>
                        );
                    })()}
                    
                    {/* 페이지 네비게이션 */}
                    <div className='flex flex-wrap gap-2 justify-center py-6 px-5'>
                        {Array.from({ length: Math.max(1, Math.ceil(items.length / pageSize)) }).map((_, i) => (
                            <button
                                key={i}
                                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                                    currentPage === i+1 
                                        ? 'bg-indigo-500 text-white shadow-md' 
                                        : 'bg-white border border-gray-300 text-gray-600 hover:border-indigo-300 hover:bg-indigo-50'
                                }`}
                                onClick={() => setCurrentPage(i+1)}
                            >
                                {i+1}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            
            <Toast 
                position='top-center'    
                status={toastStatus}
                message={toastMessage}
                isVisible={isVisible}
            />
        </main>
    );
}
